import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { removeBackground, getTaskStatus } from '../../services/chatApi';
import { Download, Upload, Sparkles, RefreshCw, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import ToolNavigator from '../../components/ToolNavigator';
import useImageTransfer from '../../hooks/useImageTransfer'; // Consistent hook usage

const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €";

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};

const RemoveBackground = () => {
    const { t } = useTranslation();
    const { user, setCredits } = useAuth();
    const location = useLocation();
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [processedImage, setProcessedImage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    
    const pollInterval = useRef(null);

    const resetParams = useCallback(() => {
        setProcessedImage(null);
        setError(null);
        setIsLoading(false);
        if (pollInterval.current) clearInterval(pollInterval.current);
    }, []);

    // 2. Activate Hook: Pass resetParams instead of resetTool
    useImageTransfer(setFile, setPreviewUrl, setProcessedImage, resetParams);

    // 3. Define resetTool: For the "New Image" / "Reset" button
    // This clears everything including the file.
    const resetTool = () => {
        setFile(null);
        setPreviewUrl(null);
        setProcessedImage(null);
        setError(null);
        setIsLoading(false);
        if (pollInterval.current) clearInterval(pollInterval.current);
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (pollInterval.current) clearInterval(pollInterval.current);
        };
    }, []);


    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setPreviewUrl(URL.createObjectURL(selectedFile));
            setProcessedImage(null);
            setError(null);
        }
    };

    const handleProcess = async () => {
        if (!file) return;
        
        setIsLoading(true);
        setError(null);
        
        try {
            // 1. Start Task
            const startResponse = await removeBackground(file);
            const taskId = startResponse.task_id;

            if (!taskId) throw new Error("No Task ID returned");

            // 2. Poll for Completion
            pollInterval.current = setInterval(async () => {
                try {
                    const statusRes = await getTaskStatus(taskId);
                    const status = statusRes.status || statusRes.state;

                    if (status === 'SUCCESS' || status === 'completed') {
                        clearInterval(pollInterval.current);
                        
                        const result = statusRes.result || statusRes.info;
                        if (result.new_credits !== undefined) {
                            setCredits(result.new_credits);
                        }
                        if (result.download_url) {
                            setProcessedImage(result.download_url);
                            setIsLoading(false);
                        } else {
                            setError("Invalid response format from server.");
                            setIsLoading(false);
                        }

                    } else if (status === 'FAILURE' || status === 'failed') {
                        clearInterval(pollInterval.current);
                        setIsLoading(false);
                        setError(t('removeBackground.errors.generic'));
                    }
                } catch (pollErr) {
                    clearInterval(pollInterval.current);
                    setIsLoading(false);
                    setError("Network error while checking status.");
                }
            }, 2000); 

        } catch (err) {
            setError(t('removeBackground.errors.generic'));
            setIsLoading(false);
        }
    };

    const handleDownload = async () => {
        if (!processedImage) return;
        try {
            const response = await fetch(processedImage);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            
            const link = document.createElement('a');
            link.href = url;
            link.download = "cleaned_image.png";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (e) {
            window.open(processedImage, '_blank');
        }
    };

    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
            {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2))? (
                <SubscribePrompt />
            ) : (
            <div className="max-w-6xl mx-auto">
                <h3 className="text-3xl font-bold text-brand-orange mb-2">
                    {t('removeBackground.title')}
                </h3>
                <p className="text-slate-600 mb-8 max-w-2xl">
                    {t('removeBackground.description')}
                </p>

                {!file ? (
                    // Upload Area
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-16 text-center hover:border-brand-orange transition-colors bg-slate-50">
                        <input
                            type="file"
                            accept="image/png, image/jpeg, image/jpg"
                            onChange={handleFileChange}
                            className="hidden"
                            id="bg-remove-upload"
                        />
                        <label htmlFor="bg-remove-upload" className="cursor-pointer flex flex-col items-center">
                            <div className="bg-white p-4 rounded-full shadow-sm mb-4">
                                <Upload size={32} className="text-brand-orange" />
                            </div>
                            <span className="text-xl font-medium text-slate-700 mb-2">{t('removeBackground.upload.label')}</span>
                            <span className="text-sm text-slate-500">{t('removeBackground.upload.hint')}</span>
                        </label>
                    </div>
                ) : (
                    // Comparison Area
                    <div className="space-y-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
                            
                            {/* Original */}
                            <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200">
                                <div className="flex justify-between items-center mb-3">
                                    <span className="font-semibold text-slate-700">{t('removeBackground.labels.original')}</span>
                                    <button onClick={resetTool} className="text-xs text-slate-500 hover:text-brand-orange">
                                        {t('removeBackground.buttons.change')}
                                    </button>
                                </div>
                                <div className="relative aspect-square bg-cover rounded-md overflow-hidden bg-slate-50">
                                    <img src={previewUrl} alt="Original" className="w-full h-full object-contain" />
                                </div>
                            </div>

                            {/* Result */}
                            <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200">
                                <div className="mb-3 flex items-center justify-between">
                                    <span className="font-semibold text-slate-700">{t('removeBackground.labels.result')}</span>
                                </div>
                                
                                <div className="relative aspect-square bg-cover rounded-md overflow-hidden flex items-center justify-center bg-brand-white">
                                    {isLoading ? (
                                        <div className="absolute inset-0 bg-white/80 flex flex-col items-center justify-center">
                                            <Loader2 className="animate-spin text-brand-orange mb-2" size={40} />
                                            <p className="text-slate-600 font-medium">{t('removeBackground.status.processing')}</p>
                                        </div>
                                    ) : processedImage ? (
                                        <img src={processedImage} alt="Result" className="w-full h-full object-contain relative z-10" />
                                    ) : (
                                        <div className="text-center p-6 bg-white/90 rounded-xl shadow-sm">
                                            <Sparkles className="mx-auto mb-2 text-slate-400" size={40} />
                                            <p className="text-slate-500">{t('removeBackground.status.waiting')}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Action Bar */}
                        <div className="flex flex-col md:flex-row gap-4 justify-center pt-4 border-t border-slate-200">
                            {!processedImage ? (
                                <button
                                    onClick={handleProcess}
                                    disabled={isLoading}
                                    className="bg-brand-orange text-white px-8 py-3 rounded-md font-semibold hover:bg-orange-600 transition flex items-center justify-center gap-2 disabled:opacity-70 min-w-[200px]"
                                >
                                    {isLoading ? <Loader2 className="animate-spin" /> : <Sparkles />}
                                    {t('removeBackground.buttons.process')}
                                </button>
                            ) : (
                                <>
                                    <button
                                        onClick={handleDownload}
                                        className="bg-green-600 text-white px-8 py-3 rounded-md font-semibold hover:bg-green-700 transition flex items-center justify-center gap-2 min-w-[200px]"
                                    >
                                        <Download size={20} />
                                        {t('removeBackground.buttons.download')}
                                    </button>
                                    <button
                                        onClick={resetTool}
                                        className="bg-slate-200 text-slate-700 px-6 py-3 rounded-md font-semibold hover:bg-slate-300 transition flex items-center justify-center gap-2"
                                    >
                                        <RefreshCw size={20} />
                                        {t('removeBackground.buttons.newImage')}
                                    </button>
                                </>
                            )}
                        </div>
                        
                        {/* Tool Navigator inserted here */}
                        {processedImage && (
                            <div className="mt-4">
                                <ToolNavigator currentImage={processedImage} />
                            </div>
                        )}

                        {error && (
                            <div className="text-center p-4 bg-red-50 text-red-600 rounded-md border border-red-200">
                                {error}
                            </div>
                        )}
                    </div>
                )}
            </div>
            )}
        </div>
    );
};

export default RemoveBackground;
